import React, { useState } from 'react';
import Modal from './Modal';

/**
 * InlineIssueMeta — staff-persona parity twin of the audit
 * `InlineQuestionMeta` findings block.
 *
 * The audit persona shows raised findings INLINE under every node (see
 * components/audits/procedure-responses/InlineQuestionMeta.tsx → the
 * `response-qmeta__section--finding` list). The staff persona raises its own
 * **vulnerabilities** (on risks) and **deficiencies** (on controls) but never
 * displayed them on screen — they hid behind a flag button that did nothing in
 * the read-only approval view. This renders that existing list inline using the
 * EXACT same `response-qmeta` CSS classes so risk/control issues read identically
 * to audit findings, and each chip opens a read-only detail popup.
 *
 * RiskVulnerability and ControlDeficiency are structurally identical, so one
 * component covers both — caller passes the label ('Vulnerabilities' /
 * 'Deficiencies') and the already-loaded list.
 */

export interface InlineIssue {
  id: number;
  title?: string | null;
  description: string;
  detected_severity: string;
  status: string;
  source?: string | null;
  source_test_name?: string | null;
  detected_on?: string | null;
  owner_name?: string | null;
  remediation_notes?: string | null;
}

// Mirrors the pill palette of the Risk Vulnerabilities / Control Deficiencies
// list tabs (RiskVulnerabilities.tsx) so the same issue reads the same colour
// wherever it appears.
const SEVERITY_CLASS: Record<string, string> = {
  Critical:      'bg-red-100 text-red-700',
  High:          'bg-orange-100 text-orange-700',
  Medium:        'bg-yellow-100 text-yellow-700',
  Low:           'bg-blue-100 text-blue-700',
  Informational: 'bg-gray-100 text-gray-600',
};

const STATUS_CLASS: Record<string, string> = {
  Open:          'bg-rose-100 text-rose-700',
  'In-Progress': 'bg-amber-100 text-amber-700',
  Resolved:      'bg-emerald-100 text-emerald-700',
  Closed:        'bg-gray-100 text-gray-600',
};

const PILL_BASE = 'px-2 py-0.5 rounded-full text-xs font-medium';

const IssueDetailModal: React.FC<{
  label: string;
  issue: InlineIssue | null;
  onClose: () => void;
}> = ({ label, issue, onClose }) => (
  <Modal isOpen={issue !== null} onClose={onClose} size="md" data-testid="MODAL_issue_detail">
    <Modal.Header>{label.replace(/ies$/, 'y').replace(/s$/, '')} detail</Modal.Header>
    <Modal.Body className="issue-detail">
      {issue && (
        <>
          {issue.title?.trim() && <h4 className="issue-detail__title">{issue.title}</h4>}
          <div className="issue-detail__pills">
            <span className={`${PILL_BASE} ${SEVERITY_CLASS[issue.detected_severity] || 'bg-gray-100 text-gray-500'}`}>
              {issue.detected_severity}
            </span>
            <span className={`${PILL_BASE} ${STATUS_CLASS[issue.status] || 'bg-gray-100 text-gray-500'}`}>
              {issue.status}
            </span>
          </div>
          <p className="issue-detail__desc">{issue.description}</p>
          <dl className="issue-detail__meta">
            <div>
              <dt>Source</dt>
              <dd>{issue.source_test_name || issue.source || '—'}</dd>
            </div>
            <div>
              <dt>Owner</dt>
              <dd>{issue.owner_name || '—'}</dd>
            </div>
            <div>
              <dt>Detected</dt>
              <dd>{issue.detected_on ? String(issue.detected_on).slice(0, 10) : '—'}</dd>
            </div>
          </dl>
          {issue.remediation_notes?.trim() && (
            <div className="issue-detail__remediation">
              <dt>Remediation</dt>
              <dd>{issue.remediation_notes}</dd>
            </div>
          )}
        </>
      )}
    </Modal.Body>
  </Modal>
);

export interface InlineIssueMetaProps {
  /** Section label, e.g. 'Vulnerabilities' (risk) or 'Deficiencies' (control). */
  label: string;
  issues: InlineIssue[];
  /** Optional node remark/observation — rendered inline as text (audit parity)
   *  instead of hiding behind the note icon. */
  remark?: string | null;
  /** Heading for the inline remark. Defaults to "Remarks:" so existing risk /
   *  control callers are unchanged; e.g. compliance steps pass "Observation:". */
  remarkLabel?: string;
  /** When set, the inline remark text is clickable and calls this (e.g. opens
   *  the same edit popup the note icon does). Display-only when omitted. */
  onRemarkClick?: () => void;
  /** Per-node testid prefix for the chip buttons. */
  testIdPrefix?: string;
}

const InlineIssueMeta: React.FC<InlineIssueMetaProps> = ({ label, issues, remark, remarkLabel = 'Remarks:', onRemarkClick, testIdPrefix }) => {
  const [detail, setDetail] = useState<InlineIssue | null>(null);

  const hasRemark = !!(remark && remark.trim());
  const hasIssues = !!issues && issues.length > 0;
  if (!hasRemark && !hasIssues) return null;

  return (
    <>
      <div className="response-qmeta">
        {hasRemark && (
          <div className="response-qmeta__section response-qmeta__section--remark">
            <span className="response-qmeta__label">{remarkLabel}</span>
            {onRemarkClick ? (
              <button
                type="button"
                className="response-qmeta__item response-qmeta__remark response-qmeta__remark--clickable"
                onClick={onRemarkClick}
                title="Click to edit remark"
              >
                <span>{remark}</span>
              </button>
            ) : (
              <span className="response-qmeta__item response-qmeta__remark">
                <span>{remark}</span>
              </span>
            )}
          </div>
        )}

        {hasIssues && (
        <div className="response-qmeta__section response-qmeta__section--finding">
          <span className="response-qmeta__label">{label}:</span>
          {issues.map((it) => (
            <button
              key={it.id}
              type="button"
              className="response-qmeta__item response-qmeta__finding-btn"
              aria-label={`View ${label}: ${it.title || it.description}`}
              data-testid={testIdPrefix ? `${testIdPrefix}_${it.id}` : undefined}
              onClick={() => setDetail(it)}
            >
              <span>{(it.title?.trim() || it.description)}</span>
            </button>
          ))}
        </div>
        )}
      </div>

      <IssueDetailModal label={label} issue={detail} onClose={() => setDetail(null)} />
    </>
  );
};

export default InlineIssueMeta;
